<template>
    <div id="merge-report-select" class="flex justify-center items-center">
        <div @click="closeByBackdrop" class="backdrop"></div>
        <div class="modal-content relative">
            <InnerLoader v-if="isLoadingModal" color="var(--text-color)" />
            <div class="modal-header flex justify-between items-center">
                <h1>{{ $t('merge_report_combined_leaderboard') }}</h1>
                <div @click="closeModal" class="close-button flex justify-center items-center cursor-pointer">
                    <i class="fas fa-times"></i>
                </div>
            </div>
            <div class="modal-body">
                <form @submit.prevent="onSubmitMergeReport">
                    <div>
                        <div class="mb-2">
                            <label for="list-name">{{ $t('merge_report_label_name') }}</label>
                            <XInput
                                @input="listName = $event.target.value"
                                :value="listName"
                                :placeholder="$t('merge_report_placeholder_name')"
                                v-validate="'required'"
                                name="name"
                                class="list-name-input mt-1"
                                id="list-name"
                            />
                            <span v-if="errors.has('name')" class="block mt-1 text-sm text-red">
                                <i class="fas fa-exclamation-circle"></i>
                                {{ $t('merge_report_name_is_required') }}
                            </span>
                        </div>
                        <div v-if="isEdit && mergeReportId">
                            <label class="mb-1" for="list-url">{{ $t('merge_report_label_url') }}</label>
                            <div class="flex items-center mb-2">
                                <XInput :value="url" class="list-name-input mr-2" disabled readonly id="list-url" />
                                <XButton @click.prevent="copyUrl" class="system-button-color system-button-text-color">
                                    <i class="fas fa-copy"></i>
                                </XButton>
                            </div>
                        </div>
                    </div>
                    <div
                        :class="{ 'max-h-490': isEdit, 'max-h-560': !isEdit }"
                        class="content-box flex flex-col sm:flex-row"
                    >
                        <div class="flex flex-col w-full">
                            <label class="mb-1" for="select-quiz">{{ $t('merge_report_label_quiz') }}</label>
                            <XSelect
                                @input="onSelectQuiz"
                                :value="selectedQuiz"
                                :options="[
                                    { id: '', label: $t('merge_report_label_all') },
                                    ...quizLists.map(quiz => ({ id: quiz._id.$oid, label: quiz.title }))
                                ]"
                                :placeholder="$t('report_thumbnail_select_quiz')"
                                :multiple="false"
                                label="label"
                                class="subject-select"
                                name="quizList"
                                id="select-quiz"
                                dense
                                outlined
                            />

                            <div class="mt-2 mb-1">{{ $t('merge_report_reports') }}</div>
                            <div id="report-tab" class="btn-group mb-2">
                                <a
                                    @click.prevent="reportType = 'user-report'"
                                    :class="{
                                        active: reportType === 'user-report',
                                        'w-full text-center font-size-12 md:font-size-14': true
                                    }"
                                >
                                    <span>{{ $t('merge_report_type_user_report') }}</span>
                                </a>
                                <a
                                    @click.prevent="reportType = 'shared-report'"
                                    :class="{
                                        active: reportType === 'shared-report',
                                        'w-full text-center font-size-12 md:font-size-14': true
                                    }"
                                >
                                    <span>{{ $t('merge_report_type_shared_report') }}</span>
                                </a>
                            </div>
                            <UserReportList
                                v-show="reportType === 'user-report'"
                                @data="onReceiveReportData"
                                @move-to-target="moveToTarget"
                                :selected-quiz="selectedQuiz"
                                :target-reports="targetReports"
                                class="report-border relative"
                                ref="userReportList"
                            />
                            <SharedReportList
                                v-show="reportType === 'shared-report'"
                                @data="onReceiveSharedReportData"
                                @move-to-target="moveSharedToTarget"
                                :selected-quiz="selectedQuiz"
                                :target-reports="targetReports"
                                class="report-border relative"
                                ref="sharedReportList"
                            />
                        </div>
                        <div class="flex flex-col w-full">
                            <div class="mb-1">{{ $t('merge_report_target_merged_reports') }}</div>
                            <div class="report-border" style="background-color: #eeeeee">
                                <Draggable
                                    v-bind="dragOptions"
                                    @start="onDragStart"
                                    @end="drag = false"
                                    :list="targetReports"
                                    :group="{ name: 'reports', pull: false, put: true }"
                                    class="list-group"
                                >
                                    <div
                                        v-for="element in targetReports"
                                        :key="element._id.$oid"
                                        :data-my-report="element.is_my_report ? 1 : 0"
                                        class="list-group-item"
                                    >
                                        <div class="flex justify-between items-center">
                                            <div class="flex flex-col title-column">
                                                <div class="text-sm font-bold mb-1">
                                                    <div class="truncate">
                                                        {{ element.name || `[${$t('report_live_play')}]` }}
                                                    </div>
                                                    <div class="truncate font-size-12 font-normal">
                                                        {{ element.quiz.title }}
                                                    </div>
                                                </div>
                                                <div class="text-xs">{{ element.created_at_readable }}</div>
                                            </div>
                                            <div class="text-sm flex items-center ml-2" style="width: 48px">
                                                <i class="far fa-user mr-1"></i>
                                                <span>{{ element.player_number || 'N/A' }}</span>
                                            </div>
                                            <div class="mx-2">
                                                <i
                                                    @click.prevent="moveToSource(element)"
                                                    class="fas fa-times cursor-pointer text-red"
                                                ></i>
                                            </div>
                                        </div>
                                    </div>
                                </Draggable>
                            </div>
                        </div>
                    </div>
                    <div class="modal-footer flex justify-between items-center mt-4">
                        <div class="flex">
                            <XButton
                                v-if="isEdit && mergeReportId"
                                @click.prevent="onDeleteMergeReport"
                                bg-color="#dc3545"
                                color="#fff"
                            >
                                <i class="fas fa-trash-alt"></i>
                            </XButton>
                        </div>
                        <div class="flex justify-end">
                            <XButton
                                @click.prevent="openMergeReportSetting"
                                bg-color="#e4e4e4"
                                color="#333"
                                class="mr-2"
                            >
                                {{ $t('merge_report_setting_button') }}
                            </XButton>
                            <XButton
                                :disabled="targetReports.length === 0"
                                type="submit"
                                class="system-button-color system-button-text-color"
                            >
                                {{
                                    isEdit && mergeReportId
                                        ? $t('merge_report_update_button')
                                        : $t('merge_report_create_button')
                                }}
                            </XButton>
                        </div>
                    </div>
                </form>
            </div>
        </div>
        <MergeReportSetting
            v-if="isOpenMergeReportSetting"
            @close="isOpenMergeReportSetting = false"
            @result="setting = $event"
            :setting="setting"
            :validate-on-close="true"
        />
    </div>
</template>

<script>
import { mapGetters } from 'vuex'
import Draggable from 'vuedraggable'
import XSelect from '@/components/v2/XSelect.vue'
import XInput from '@/components/v2/XInput.vue'
import XButton from '@/components/v2/XButton.vue'
import popup from '@/lib/popup'
import MergeReportSetting from '@/components/merge-report/MergeReportSetting'
import UserReportList from '@/components/merge-report/UserReportList'
import SharedReportList from '@/components/merge-report/SharedReportList'
import InnerLoader from '@/components/InnerLoader'

export default {
    props: {
        isEdit: {
            type: Boolean,
            default: false
        },
        mergeReportId: {
            type: String,
            default: null
        }
    },

    components: {
        Draggable,
        XSelect,
        XInput,
        XButton,
        MergeReportSetting,
        InnerLoader,
        UserReportList,
        SharedReportList
    },

    data() {
        return {
            listName: '',
            url: '',
            selectedQuiz: {
                id: '',
                label: this.$t('merge_report_label_all')
            },
            reportType: 'user-report', // user-report, shared-report
            sourceReports: [],
            sourceSharedReports: [],
            targetReports: [],
            drag: false,
            isOpenMergeReportSetting: false,
            isLoadingModal: false,
            setting: {
                displayRanking: 10,
                reloadTime: 2,
                layout: 'list',
                status: true,
                mergePlayerOption: 'same_name_device_id',
                showRankingTime: 10,
                displayMultipleResponseWith: 'best'
            }
        }
    },

    computed: {
        ...mapGetters({
            quizLists: 'getQuizByUser',
            originalUserReports: 'getOriginalUserReports',
            originalSharedReports: 'getOriginalSharedReports'
        }),

        dragOptions() {
            return {
                animation: 200,
                ghostClass: 'ghost'
            }
        }
    },

    async mounted() {
        await this.getMergeReportById()
        this.loadReports(this.selectedQuiz)
    },

    methods: {
        closeModal() {
            if (this.isLoadingModal) {
                return
            }

            this.$emit('close')
        },

        closeByBackdrop(e) {
            if (!e.target.classList.contains('backdrop')) {
                return
            }

            this.closeModal()
        },

        async getMergeReportById() {
            if (this.isEdit && this.mergeReportId) {
                try {
                    this.isLoadingModal = true

                    const res = await this.$store.dispatch('getMergeReportById', {
                        id: this.mergeReportId
                    })

                    this.listName = res.name
                    this.url = res.url_report
                    this.targetReports = res.reports
                    this.setting.displayRanking = res.display_ranking
                    this.setting.reloadTime = res.time_to_reload
                    this.setting.layout = res.layout
                    this.setting.status = res.status
                    this.setting.mergePlayerOption = res.merge_player_option
                    this.setting.showRankingTime = res.show_ranking_time
                    this.setting.displayMultipleResponseWith = res.display_multiple_response_with

                    this.isLoadingModal = false
                } catch {
                    this.isLoadingModal = false
                    this.toastError(this.$t('merge_report_get_report_error'))
                }
            }
        },

        onSelectQuiz(event) {
            this.loadReports(event)

            this.$nextTick(() => {
                // Try to find and blur the search input directly
                const searchInput = document.querySelector('#select-quiz .vs__search')
                if (searchInput) {
                    searchInput.blur()
                }
            })
        },

        loadReports(event) {
            this.selectedQuiz = event
            this.$refs.userReportList.clearList()
            this.$refs.sharedReportList.clearList()
            this.$store.commit('setOriginalUserReports', [])
            this.$store.commit('setOriginalSharedReports', [])

            let isSharedReport = false

            if (event && event.id) {
                const currentReport = this.quizLists.find(quiz => quiz._id.$oid === event.id)
                this.reportType = 'user-report'

                if (currentReport && currentReport.is_shared_report) {
                    isSharedReport = true
                    this.reportType = 'shared-report'
                }
            }

            if (this.selectedQuiz) {
                this.$nextTick(() => {
                    if (!isSharedReport) {
                        this.$refs.userReportList.getReportByQuiz()
                    }

                    this.$refs.sharedReportList.getSharedReport()
                })
            }
        },

        onReceiveReportData(reports) {
            this.sourceReports = reports
        },

        onReceiveSharedReportData(reports) {
            this.sourceSharedReports = reports
        },

        copyUrl() {
            navigator.clipboard.writeText(this.url).then(() => {
                this.toastSuccess(this.$t('merge_report_link_copied'))
            })
        },

        openMergeReportSetting() {
            this.isOpenMergeReportSetting = true
        },

        onDeleteMergeReport() {
            popup.confirm(this.$t('merge_report_confirm_delete'), async () => {
                try {
                    const payload = {
                        id: this.mergeReportId
                    }

                    await this.$store.dispatch('deleteMergeReport', payload)
                    this.toastSuccess(this.$t('merge_report_delete_success'))
                    this.$emit('success', 'delete')
                } catch {
                    this.toastError(this.$t('merge_report_delete_error'))
                }
            })
        },

        moveToTarget(element) {
            const index = this.sourceReports.findIndex(item => item._id.$oid === element._id.$oid)
            this.targetReports.push(this.sourceReports[index])
            this.sourceReports.splice(index, 1)
        },

        moveSharedToTarget(element) {
            const index = this.sourceSharedReports.findIndex(item => item._id.$oid === element._id.$oid)
            this.targetReports.push(this.sourceSharedReports[index])
            this.sourceSharedReports.splice(index, 1)
        },

        moveToSource(element) {
            const isInOriginalUserReports = this.originalUserReports.some(
                report => report._id.$oid === element._id.$oid
            )

            const isInOriginalSharedReports = this.originalSharedReports.some(
                report => report._id.$oid === element._id.$oid
            )

            const index = this.targetReports.findIndex(item => item._id.$oid === element._id.$oid)

            if (isInOriginalUserReports) {
                this.sourceReports.push(this.targetReports[index])
                this.reportType = 'user-report'
            } else if (isInOriginalSharedReports) {
                this.sourceSharedReports.push(this.targetReports[index])
                this.reportType = 'shared-report'
            }

            this.targetReports.splice(index, 1)
        },

        onDragStart() {
            this.drag = true
        },

        async onSubmitMergeReport() {
            const validateResult = await this.$validator.validate()

            if (!validateResult) return

            if (this.isEdit && this.mergeReportId) {
                try {
                    this.isLoadingModal = true

                    const reportIds = this.targetReports.map(item => ({
                        report_id: item._id.$oid,
                        quiz_id: item.quiz._id.$oid
                    }))

                    const payload = {
                        id: this.mergeReportId,
                        name: this.listName,
                        report_ids: reportIds,
                        display_ranking: this.setting.displayRanking,
                        time_to_reload: this.setting.reloadTime,
                        layout: this.setting.layout,
                        status: this.setting.status,
                        merge_player_option: this.setting.mergePlayerOption,
                        show_ranking_time: this.setting.showRankingTime,
                        display_multiple_response_with: this.setting.displayMultipleResponseWith
                    }

                    await this.$store.dispatch('updateMergeReport', payload)
                    this.toastSuccess(this.$t('merge_report_update_success'))
                    this.$emit('success', 'update')

                    this.isLoadingModal = false
                } catch {
                    this.toastError(this.$t('merge_report_update_error'))
                    this.isLoadingModal = false
                }
            } else {
                try {
                    this.isLoadingModal = true

                    const reportIds = this.targetReports.map(item => ({
                        report_id: item._id.$oid,
                        quiz_id: item.quiz._id.$oid
                    }))

                    const payload = {
                        name: this.listName,
                        report_ids: reportIds,
                        display_ranking: this.setting.displayRanking,
                        time_to_reload: this.setting.reloadTime,
                        layout: this.setting.layout,
                        status: this.setting.status,
                        merge_player_option: this.setting.mergePlayerOption,
                        show_ranking_time: this.setting.showRankingTime,
                        display_multiple_response_with: this.setting.displayMultipleResponseWith
                    }

                    await this.$store.dispatch('createMergeReport', payload)
                    this.toastSuccess(this.$t('merge_report_create_success'))
                    this.$emit('success', 'create')

                    this.isLoadingModal = false
                } catch {
                    this.toastError(this.$t('merge_report_create_error'))
                    this.isLoadingModal = false
                }
            }
        }
    }
}
</script>

<style lang="scss">
#merge-report-select {
    position: fixed;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    z-index: 100;

    .backdrop {
        position: absolute;
        top: 50%;
        left: 50%;
        transform: translate(-50%, -50%);
        width: 100%;
        height: 100%;
        background-color: #0006;
        z-index: -1;
    }

    .modal-content {
        display: flex;
        flex-direction: column;
        width: 800px;
        border-radius: 6px;
        color: #333;
        background-color: #fff;
        margin: 0.5rem;

        .modal-header {
            padding: 14px 14px 0 14px;

            h1 {
                margin: 0;
                font-size: 17.5px;
            }

            .close-button {
                width: 28px;
                height: 28px;
                border-radius: 50%;
                color: #6c757d;
                transition: color 0.2s, background-color 0.2s;

                &:hover {
                    color: #343a40;
                    background: #e9ecef;
                }
            }
        }

        .modal-body {
            padding: 14px;

            .content-box {
                height: calc(100vh - 300px);
                overflow-y: auto;
                gap: 1rem;

                @media screen and (max-width: 575px) {
                    height: calc(100vh - 350px);
                }
            }

            .max-h-560 {
                max-height: 560px;
            }

            .max-h-490 {
                max-height: 490px;
            }

            .report-border {
                border-radius: 4px;
                border: 2px solid #ccc;
                height: 100%;
                overflow-y: auto;

                @media screen and (max-width: 575px) {
                    height: 200px;
                }
            }

            .list-name-input {
                padding: 0.375rem 0.75rem;
                border: 1px solid #ccc;
                font-size: 1rem;
                font-weight: 400;
                color: #000;
                text-shadow: none;
            }

            #select-quiz {
                padding: 0.3rem;
                border-radius: 4px;
                border: 1px solid #ccc;

                input {
                    font-size: 16px;
                }

                & .vs__selected {
                    margin: 0;
                }

                & .vs__no-options {
                    font-size: 16px;
                }
            }

            .list-group {
                min-height: 100%;
                overflow: hidden;
            }

            .list-group-item {
                cursor: move;
                padding: 0.5rem;
                margin: 0.5rem;
                border-radius: 4px;
                border: 2px solid #ccc;
                background-color: #fff;

                .title-column {
                    width: 220px;

                    @media screen and (max-width: 425px) {
                        width: 150px;
                    }
                }
            }

            .ghost {
                opacity: 0.3;
                background: #fff;
            }

            .no-source-report {
                color: #ccc;
                position: absolute;
                top: 50%;
                left: 50%;
                transform: translate(-50%, -50%);
            }

            #report-tab {
                display: flex;
                border-radius: 4px;
                justify-content: center;
                align-items: center;
            }
        }
    }
}
</style>
